import {
    JOB_COSTING_MODE,
    getJobCostingMode,
    normalizeARCharge,
    normalizeAPCharge,
    normalizeARCharges,
    normalizeAPCharges,
} from "@/pages/JobCosting/jobCostingUtils";

export { getJobCostingMode, JOB_COSTING_MODE };

/** Backend accounting division values (must match backend/accounting). */
export const ACCOUNTING_DIVISION = {
    EXPORT: "Export",
    IMPORT: "Import",
};

export function getAccountingDivision(mode) {
    return mode === JOB_COSTING_MODE.IMPORT
        ? ACCOUNTING_DIVISION.IMPORT
        : ACCOUNTING_DIVISION.EXPORT;
}

/** Map backend division (Export/Import) to invoice metadata label. */
export function getInvoiceMetadataDivision(accountingDivision) {
    if (accountingDivision === ACCOUNTING_DIVISION.IMPORT) return "OCEAN IMPORT";
    if (accountingDivision === ACCOUNTING_DIVISION.EXPORT) return "OCEAN EXPORT";
    return accountingDivision || "";
}

/** Map metadata division label (or Export/Import) to backend accounting division. */
export function getAccountingDivisionFromMetadata(metadataDivision) {
    if (!metadataDivision) return null;
    const raw = String(metadataDivision).trim();
    if (raw === ACCOUNTING_DIVISION.IMPORT || raw === ACCOUNTING_DIVISION.EXPORT) {
        return raw;
    }
    const upper = raw.toUpperCase();
    if (upper.includes("IMPORT")) return ACCOUNTING_DIVISION.IMPORT;
    if (upper.includes("EXPORT")) return ACCOUNTING_DIVISION.EXPORT;
    return null;
}

/** Query string for accounting bill/invoice APIs (company_id + division). */
export function buildAccountingQueryParams(companyId, division) {
    const params = new URLSearchParams();
    if (companyId != null && companyId !== "") {
        params.set("company_id", String(companyId));
    }
    if (division) {
        params.set("division", division);
    }
    const qs = params.toString();
    return qs ? `?${qs}` : "";
}

/** Stable charge key for accounting sync maps (id, ID, or rowid). */
export function getChargeKey(charge) {
    const id = charge?.id ?? charge?.ID ?? charge?.rowid;
    if (id === undefined || id === null || id === "") return "";
    return String(id);
}

export function buildBookingAccountingStatusUrl(apiBaseUrl, bookingId, companyId, division) {
    const params = new URLSearchParams({
        company_id: String(companyId ?? 1),
        division: division || ACCOUNTING_DIVISION.EXPORT,
    });
    return `${apiBaseUrl}/api/accounting/sync/booking/${bookingId}?${params.toString()}`;
}

/** Map accounting API status onto charge rows (export camelCase or import UPPER_SNAKE). */
export function mapAccountingStatusOntoCharges(arCharges, apCharges, statusData) {
    const arMap = statusData?.ar_charge_map || {};
    const apMap = statusData?.ap_charge_map || {};

    const nextAr = arCharges.map((charge) => {
        const key = getChargeKey(charge);
        const invoiceInfo = key ? arMap[key] : undefined;
        if (!invoiceInfo) return charge;

        const patch = {
            invoiceId: invoiceInfo.invoice_id,
            invoiceNo: invoiceInfo.invoice_no,
            invoiceStatus:
                invoiceInfo.invoice_status === "paid"
                    ? "invoiced"
                    : invoiceInfo.invoice_status === "issued"
                        ? "invoiced"
                        : "draft",
            INVOICE_ID: invoiceInfo.invoice_id,
            INVOICE_NO: invoiceInfo.invoice_no,
            INVOICE_STATUS:
                invoiceInfo.invoice_status === "paid"
                    ? "invoiced"
                    : invoiceInfo.invoice_status === "issued"
                        ? "invoiced"
                        : "draft",
        };
        return { ...charge, ...patch };
    });

    const nextAp = apCharges.map((charge) => {
        const key = getChargeKey(charge);
        const billInfo = key ? apMap[key] : undefined;
        if (!billInfo) return charge;

        const patch = {
            billId: billInfo.bill_id,
            billNo: billInfo.bill_no,
            billStatus:
                billInfo.bill_status === "paid"
                    ? "billed"
                    : billInfo.bill_status === "approved"
                        ? "billed"
                        : "draft",
            BILL_ID: billInfo.bill_id,
            BILL_NO: billInfo.bill_no,
            BILL_STATUS:
                billInfo.bill_status === "paid"
                    ? "billed"
                    : billInfo.bill_status === "approved"
                        ? "billed"
                        : "draft",
        };
        return { ...charge, ...patch };
    });

    return { arCharges: nextAr, apCharges: nextAp };
}

/** Charges eligible for AR sync (has bill-to party; mirrors export booking filter). */
export function filterARChargesForSync(charges) {
    return charges.filter((c) => {
        const n = normalizeARCharge(c);
        return (
            n.billToPartyId &&
            n.billToPartyId !== 0 &&
            n.invoiceId !== "" &&
            n.invoiceId !== null
        );
    });
}

/** Charges eligible for AP sync (has vendor; mirrors export booking filter). */
export function filterAPChargesForSync(charges) {
    return charges.filter((c) => {
        const n = normalizeAPCharge(c);
        return (
            n.vendorId &&
            n.vendorId !== 0 &&
            n.billId !== "" &&
            n.billId !== null
        );
    });
}

/** Normalize import/export charge rows to camelCase for accounting POST bodies. */
export function chargesForAccountingApi(charges, type = "ar") {
    const list = Array.isArray(charges) ? charges : [];
    const normalized =
        type === "ap" ? normalizeAPCharges(list) : normalizeARCharges(list);
    return normalized.map((c) => ({
        ...c,
        id: getChargeKey(c) || c.id,
    }));
}

export function buildARSyncBody({
    bookingId,
    bookingNo,
    charges,
    customerId,
    customerName,
    companyId,
    division,
}) {
    return {
        booking_id: String(bookingId),
        booking_no: bookingNo,
        charges: chargesForAccountingApi(charges, "ar"),
        customer_id: String(customerId ?? ""),
        customer_name: customerName,
        company_id: companyId ?? 1,
        division: division || ACCOUNTING_DIVISION.EXPORT,
    };
}

export function findChargeSyncUpdate(updatedCharges, charge) {
    const key = getChargeKey(charge);
    if (!key || !Array.isArray(updatedCharges)) return undefined;
    return updatedCharges.find((u) => String(u.charge_id) === key);
}

export function buildAPSyncBody({
    bookingId,
    bookingNo,
    charges,
    vendorId,
    vendorName,
    companyId,
    division,
}) {
    return {
        booking_id: String(bookingId),
        booking_no: bookingNo,
        charges: chargesForAccountingApi(charges, "ap"),
        vendor_id: String(vendorId ?? ""),
        vendor_name: String(vendorName ?? ""),
        company_id: companyId ?? 1,
        division: division || ACCOUNTING_DIVISION.EXPORT,
    };
}
